import {
    getStickyLeftPosition,
    getStickyRightPosition,
} from './helpers/stickyColumnHelpers'
import { TableContext } from '../../../../providers'
import { Column } from '../../../../types'
import { useRef, useState, useEffect, useContext } from 'react'

interface StickyStyling {
    left?: string
    right?: string
}

export const useStickyColumn = (column?: Column) => {
    const ref = useRef(null)
    const [styling, setStyling] = useState<StickyStyling>({})
    const { columns, columnWidths } = useContext(TableContext)

    const isStickyLeft = (className: string) => className.includes('left')
    const isStickyRight = (className: string) => className.includes('right')
    const isStickyBorderless = (className: string) => className.includes('borderless')

    const setStickyPosition = () => {
        if (!column?.sticky || !ref?.current || !columnWidths) {
            return
        }

        const newStyling: StickyStyling = {}
        if (isStickyLeft(column.sticky)) newStyling.left = getStickyLeftPosition(column, columns, columnWidths) ?? ''
        if (isStickyRight(column.sticky)) newStyling.right = getStickyRightPosition(column, columns, columnWidths) ?? ''

        setStyling(newStyling)
    }

    const isLastLeftStickyColumn = (column: Column): boolean =>
        columns.filter((column) => column.sticky?.includes('left')).at(-1)?.id === column.id
    const isFirstRightStickyColumn = (column: Column): boolean =>
        columns.filter((column) => column.sticky?.includes('right')).at(0)?.id === column.id

    const getStickyClasses = (classPrefix: string): string => {
        let classes = ''
        if (column?.sticky) {
            classes += `${classPrefix}--sticky`
            if (isStickyLeft(column.sticky)) {
                classes += ` ${classPrefix}--sticky-left`
                if (isLastLeftStickyColumn(column)) classes += ` ${classPrefix}--sticky-left-last`
            }
            if (isStickyRight(column.sticky)) {
                classes += ` ${classPrefix}--sticky-right`
                if (isFirstRightStickyColumn(column)) classes += ` ${classPrefix}--sticky-right-first`
            }

            if (isStickyBorderless(column.sticky)) classes += ` ${classPrefix}--sticky__borderless`
        }

        return classes
    }

    useEffect(() => {
        setStickyPosition()
    }, [ref, columnWidths])

    return {
        getStickyClasses,
        setStickyPosition,
        ref,
        styling,
    }
}
